import type { FbUrlParams } from 'db://assets/src/platform/api/fb/FBPlatform';
import type { OkUrlParams } from 'db://assets/src/platform/api/ok/OkPlatform';
import type { VkUrlParams } from 'db://assets/src/platform/api/vk/VkPlatform';

export type UrlParam =
	| 'language'
	| 'adminPanel'
	| 'throttle'
	| 'profile'
	| 'clearProfile'
	| 'start' // стартовая сцена после загрузки игры
	| 'stats'
	| 'stateDevTools'
	| 'gems'
	| 'afkTime' // время между запусками игры (в минутах)
	| 'afkScreen' // 0 - afk экран не показывается никогда, 1 - показывается всегда (даже если прошла 0.01 секунда)
	| 'skipChest'
	| 'mysteryBarrel' // интервал между появлениями mystery barrel (в секундах)
	| 'mysteryBarrelType' // например, ad_coins или instant_worker
	| 'screen' // какой экран открыть после загрузки уровня
	| 'sentry' // sentry=1 включает Сентри для personal env
	| 'sentryDebug' // вывод дополнительных логов для Сентри
	| 'userId' // фейковый userId для тестирования клиент-серверного взаимодействия
	| 'storage' // вид persistent storage, опции - ls (local storage), idb (indexed-db) & server (используется по умолчанию)
	| 'storageTimer' // как часто сохраняем профиль в persistent storage (в мс)
	| 'viewerId' // id игрока для авторизации
	| 'sig' // подпись для авторизации
	| 'bannerAd'
	| 'bannerAdPosition'
	| 'bannerAdSize'
	| MainResources
	| EventResources
	| TutorsParams
	| AdsUrlParams
	| OkUrlParams
	| VkUrlParams
	| FbUrlParams;

type MainResources = 'coins' | 'magicDust' | 'jettons';

type EventResources = 'eventCoins' | 'eventMagicDust' | 'eventJettons';

type TutorsParams =
	| 'tutors' // tutors=0 to block all tutors
	| 'tutorReset' // tutorReset=2a or tutorReset=1,2-a,3 to reset tutor(s) state
	| 'tutorComplete' // tutorComplete=2a or tutorComplete=1,2-a,3 to mark tutor(s) as complete
	| 'tutorDisable'; // tutorDisable=2a or tutorDisable=1,2-a,3 to disable tutor(s)

type AdsUrlParams =
	| 'noAds' // включает/выключает noAds бонус
	| 'fakeAds' // использовать fake ads вместо рекламы от платформы (ok, vk...)
	| 'skipInters' // пропускаем показ interstitial ads
	| 'skipRewarded' // пропускаем показ rewarded ads
	| 'skipAds'; // пропускаем показ любых ads

export class UrlParams {
	private static usp: URLSearchParams & { originalSearch?: string };

	public static get(name: UrlParam): string | null {
		const search = window.location.search;

		if (!this.usp || this.usp.originalSearch !== search) {
			this.usp = new URLSearchParams(search);
			this.usp.originalSearch = search;
		}

		return this.usp.get(name);
	}

	public static has(name: UrlParam): boolean {
		return this.get(name) !== null;
	}

	public static getNumber(name: UrlParam, _default = 0): number {
		const value = this.get(name);
		if (value) {
			const num = parseFloat(value);
			return isNaN(num) ? _default : num;
		}

		return _default;
	}

	public static getNumbers(name: UrlParam, _default?: number[]): number[] | null {
		const value = this.get(name);
		if (!value) {
			return _default ?? null;
		}

		if (value[0] !== '[' || value[value.length - 1] !== ']') {
			console.warn('Malformed url param input', value);
			return null;
		}

		const content = value.slice(1, value.length - 1);
		return content.split(',').map((numStr) => {
			return parseFloat(numStr);
		});
	}

	public static getBool(name: UrlParam, expectedValue = '1'): boolean {
		const value = this.get(name);

		return !!(value && value === expectedValue);
	}
}
